import React, {memo, useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import dynamic from 'next/dynamic';
import {MUIBox} from '@/components/MUI-Components';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import {BADGE_SLIDES} from '@/constants/badges';
import styles from '@/styles/blogTocSidebar.module.scss';

const DisplayImage = dynamic(() => import('@/components/Ui-components/display-image'));

type BadgePreview = {src: string; alt: string; left: number; top: number};

// How far the reader is through the article body. Its own component so a scroll only re-renders this
// bar — held by the page, every scroll event re-rendered the whole post.
const ReadingProgress = memo(() => {
    const [readingProgress, setReadingProgress] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            const section = document.getElementById('bdn-description-section');
            if (!section) return;
            const scrolled = globalThis.scrollY - (section.offsetTop || 0);
            const progress = Math.min(100, Math.max(0, Math.round((scrolled / section.offsetHeight) * 100)));
            setReadingProgress(progress);
        };
        globalThis.addEventListener('scroll', handleScroll, {passive: true});
        return () => globalThis.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <MUIBox className="bdn-toc-progress-block">
            <MUIBox className="bdn-toc-progress-row">
                <span className="bdn-toc-progress-label">Progress</span>
                <span className="bdn-toc-progress-pct">{readingProgress}%</span>
            </MUIBox>
            <MUIBox className="bdn-toc-progress-bar">
                <MUIBox className="bdn-toc-progress-fill" style={{width: `${readingProgress}%`}} />
            </MUIBox>
        </MUIBox>
    );
});
ReadingProgress.displayName = 'ReadingProgress';

// Left column: table of contents, reading progress, and award-badge marquee.
// Presentational — JSX copied verbatim. `tocListRef` is forwarded to the same
// <nav> element so the page's scroll-into-view effect keeps working unchanged.
const BlogTocSidebar = ({
    tocItems,
    activeIndex,
    handleTocClick,
    tocListRef
}: {
    tocItems: string[];
    activeIndex: number | null;
    handleTocClick: (index: number) => void;
    tocListRef: React.RefObject<HTMLElement>;
}) => {
    const [mounted, setMounted] = useState(false);
    const [preview, setPreview] = useState<BadgePreview | null>(null);

    useEffect(() => setMounted(true), []);

    const showPreview = (e: React.MouseEvent<HTMLElement>, badge: {src: string; alt: string}) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setPreview({
            src: badge.src,
            alt: badge.alt,
            left: rect.left + rect.width / 2,
            top: rect.top
        });
    };

    return (
        <MUIBox className="bdn-toc-wrapper">
            <MUIBox className="bdn-toc-container">
                <MUIBox className="bdn-toc-header">
                    <MenuBookOutlinedIcon className="bdn-toc-book-icon" />
                    <span className="bdn-toc-title">ON THIS PAGE</span>
                </MUIBox>
                <nav className="bdn-toc-list" ref={tocListRef} data-lenis-prevent>
                    {tocItems.map((item, i) => (
                        <button
                            key={i}
                            className={`bdn-toc-item${activeIndex === i ? ' bdn-toc-item--active' : ''}`}
                            onClick={() => handleTocClick(i)}
                        >
                            <span className="bdn-toc-num">{String(i + 1).padStart(2, '0')}</span>
                            <span className="bdn-toc-text">{item}</span>
                        </button>
                    ))}
                </nav>
            </MUIBox>
            <ReadingProgress />
            {/* ── Award badges marquee (below TOC) ── */}
            <MUIBox className="bdn-badge-slider">
                <MUIBox className="bdn-badge-track">
                    {[...BADGE_SLIDES, ...BADGE_SLIDES].map((badge, i) => (
                        <MUIBox
                            key={i}
                            className="bdn-badge-item"
                            aria-hidden={i >= BADGE_SLIDES.length}
                            onMouseEnter={(e: React.MouseEvent<HTMLElement>) => showPreview(e, badge)}
                            onMouseLeave={() => setPreview(null)}
                        >
                            <DisplayImage src={badge.src} alt={badge.alt} width={80} height={80} quality={90} />
                        </MUIBox>
                    ))}
                </MUIBox>
            </MUIBox>
            {mounted &&
                preview &&
                createPortal(
                    <MUIBox className={`bdn-badge-preview ${styles.bdnBadgePreview}`} style={{left: preview.left, top: preview.top}}>
                        <DisplayImage src={preview.src} alt={preview.alt} width={200} height={200} quality={90} />
                    </MUIBox>,
                    document.body
                )}
        </MUIBox>
    );
};

export default BlogTocSidebar;
